import { emitter, useIsDarkMode } from '@flowus/common';
import { sleep } from '@flowus/common/async';
import {
  checkAiSpaceStatus,
  fetchAiSpaceStatus,
} from '@flowus/common/chat-ai/hooks/ai-limit-store';
import {
  ConversationContext,
  defaultConversationContext,
} from '@flowus/common/chat-ai/hooks/context';
import { useLiteConversationIdByParentId } from '@flowus/common/chat-ai/hooks/hook';
import { fetchLiteChatByParentId, setAiChatPages } from '@flowus/common/chat-ai/hooks/store';
import { ConversationTools } from '@flowus/common/chat-ai/hooks/store-tools';
import type { ConversationContextType } from '@flowus/common/chat-ai/hooks/types';
import { AI_PAGE_ID, ChatUIStyle, EditorNodeType } from '@flowus/common/chat-ai/hooks/types';
import { useChatTranslation } from '@flowus/common/chat-ai/hooks/use-i18n';
import { getPageTitleInfo } from '@flowus/common/chat-ai/hooks/use-search';
import { LiteChatPageWithI18n } from '@flowus/common/chat-ai/pages/lite-chat-page';
import { AiPage } from '@flowus/common/components/ai-page';
import { NextModalProvider } from '@flowus/common/next-modal';
import { SearchParams } from '@flowus/login/const';
import type { BlockDTO } from '@next-space/fe-api-idl';
import { BlockType, QASearchSource } from '@next-space/fe-api-idl';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { isMobile } from 'react-device-detect';
import { useAiChat } from 'src/hooks/ai/use-ai-chat';
import { useReadonly } from 'src/hooks/page';
import { useCurrentSpace } from 'src/hooks/space';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { blocksActions } from 'src/redux/reducers/blocks';
import { cache, dispatch } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { useObservableBlock } from 'src/services/rxjs-redux/use-obs-block';
import { judgeSharePage, judgeSubscribePage, useGetPageId } from 'src/utils/getPageId';
import { usePickBlock } from 'src/utils/pick-block';
import { useInWiki, useInWikiContent } from 'src/views/main/aside/context';
import { usePersonalWikiId } from 'src/views/main/aside/wiki/use-personal-wiki-id';
import { useSetDrawerOpenState } from 'src/views/main/drawer/shared';

export const HELPER_CANTER_ID = 'e68380be-23cf-42c4-9b95-23a52713213d';
// const OFFICIAL_SPACE_ID = 'e5418b54-223f-4f62-933f-cc5da586e910';

export const AiChatPage: FC = () => {
  return <BuildInAiChatPage />;
};

const FuAiChatPage: FC = () => {
  const isDarkModel = useIsDarkMode();
  const pageId = useGetPageId();
  const isSubscribePage = judgeSubscribePage();
  const isSharePage = judgeSharePage();
  const spaceId = useObservableBlock(pageId, (s) => {
    return s?.spaceId;
  });
  const [receiveParams, setReceiveParams] = useState<URLSearchParams>();

  const helperCenterId = useMemo(() => tryGetHelperCenterId(pageId), [pageId]);

  useEffect(() => {
    // 搜索框动态传送过来的参数
    const onReceive = (data: any) => {
      const { prompt, pageId } = data;
      const urlParams = new URLSearchParams();
      urlParams.append(SearchParams.defaultPrompt, prompt);
      urlParams.append(SearchParams.pageId, pageId);
      setReceiveParams(urlParams);
    };
    emitter.on('aiPage', onReceive);
    return () => {
      emitter.off('aiPage', onReceive);
    };
  }, []);

  const params = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    const params = new URLSearchParams();
    params.append(SearchParams.spaceId, getCurrentSpaceId());
    if (isSubscribePage) {
      params.append(SearchParams.aiType, 'subscribe');
    } else if (isSharePage && spaceId) {
      // 只有知识库页面才会显示
      params.append(SearchParams.aiType, 'knowledge');
      params.delete(SearchParams.spaceId);
      params.append(SearchParams.spaceId, spaceId);
      params.append(SearchParams.pageId, pageId);
    }
    const prompt = urlParams.get(SearchParams.defaultPrompt);
    prompt && params.append(SearchParams.defaultPrompt, prompt);
    if (helperCenterId) {
      params.append(SearchParams.hint, 'help');
    }
    return params;
  }, [helperCenterId, isSharePage, isSubscribePage, pageId, spaceId]);

  let combineParams = params.toString();
  if (receiveParams) {
    receiveParams.forEach((v, k) => {
      params.delete(k);
      if (v) {
        params.set(k, v);
      }
    });
    combineParams = params.toString();
  }

  return <AiPage isDarkModel={isDarkModel} pageId={pageId} addonUrlParams={combineParams} />;
};

export const tryGetHelperCenterId = (pageId: string) => {
  let b = cache.blocks[pageId];
  let count = 0;
  while (b) {
    if (b.uuid === HELPER_CANTER_ID) return HELPER_CANTER_ID;
    b = cache.blocks[b.parentId];
    count++;
    if (count > 50) return;
  }
};

export const BuildInAiChatPage: FC = () => {
  const pageId = useGetPageId();
  const inWiki = useInWiki();
  const personalWikiId = usePersonalWikiId();
  const isPersonalWiki = pageId === personalWikiId;
  const {
    onSaveArticle,
    renderPageIcon,
    insertParagraph,
    openUpgrade,
    onRenderBlockPreview,
    openAICoverToShare,
  } = useAiChat();
  const currentSpace = useCurrentSpace();
  const setDrawerState = useSetDrawerOpenState();
  const liteConversationId = useLiteConversationIdByParentId(pageId);
  const updateBlocks = useCallback((blocks?: Record<string, BlockDTO>) => {
    dispatch(blocksActions.update({ blocks: blocks as Record<string, NextBlock> }));
  }, []);
  const [context, setContext] = useState<ConversationContextType>({
    ...defaultConversationContext,
    conversationId: liteConversationId || AI_PAGE_ID,
    style: ChatUIStyle.LITE,
    spaceId: currentSpace.uuid,
    parentId: pageId,
    inWiki,
    personalWikiId,
    isPersonalWiki,
  });
  const block = usePickBlock(pageId);
  const readonly = useReadonly(block?.uuid);
  const { t } = useChatTranslation();

  const updateContext = useCallback((context: Partial<ConversationContextType>) => {
    setContext((prev) => ({ ...prev, ...context }));
  }, []);

  const navigateToChat = useCallback(
    (conversationId: string) => {
      updateContext({ conversationId });
    },
    [updateContext]
  );

  const onClose = useCallback(() => {
    if (inWiki) {
      emitter.emit('closeWikiAIPanel');
    } else {
      setDrawerState(false);
    }
  }, [inWiki, setDrawerState]);

  const onSummarize = useCallback(() => {
    const conversationTools = new ConversationTools(context.conversationId);
    const editor = conversationTools.getEditor();
    if (editor) {
      editor.commands.focus();
      const editorText = editor.getText();
      const command = t('chatHomeSummarize');
      if (!editorText.includes(command)) {
        editor.commands.insertContent(`${command} `);
      }

      const label = getPageTitleInfo(cache.blocks[pageId] as BlockDTO).title;

      if (editorText.includes(label)) {
        editor.commands.insertContent(`@`);
      } else {
        conversationTools.addSearchBlocks({ type: 'page', uuid: pageId });
        editor.commands.insertContent({
          type: EditorNodeType.MENTION,
          attrs: {
            id: pageId,
            label,
          },
        });
        editor.commands.insertContent(` `);
      }
    }
  }, [context.conversationId, pageId, t]);

  useEffect(() => {
    updateContext({ spaceId: currentSpace.uuid });
  }, [currentSpace.uuid, updateContext]);

  useEffect(() => {
    void sleep(0).then(async () => {
      // 自动把当前页面加进去
      const { conversationId, blocks } = await fetchLiteChatByParentId(pageId);
      if (conversationId) updateContext({ conversationId });
      if (blocks) {
        dispatch(blocksActions.update({ blocks: blocks as Record<string, NextBlock> }));
      }
      setAiChatPages({ [pageId]: cache.blocks[pageId] as BlockDTO });
      if (checkAiSpaceStatus(currentSpace.uuid) || inWiki) {
        const id = conversationId || AI_PAGE_ID;
        const tools = new ConversationTools(id);
        tools.updateQaQueryOption({
          networkSearch: false,
          searchBlocks: [{ type: 'page', uuid: pageId }],
          qaSearchSource: QASearchSource.NONE,
        });
        tools.scrollToBottom();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  useEffect(() => {
    // 自动纠正的话，需要满足条件。
    // 当前是默认 id & 并且要更新的 id 不是默认 id
    if (
      context.conversationId === AI_PAGE_ID &&
      liteConversationId !== AI_PAGE_ID &&
      liteConversationId
    ) {
      updateContext({ conversationId: liteConversationId });
    }
  }, [context.conversationId, liteConversationId, updateContext]);

  useEffect(() => {
    void fetchAiSpaceStatus(currentSpace.uuid);
  }, [currentSpace.uuid]);
  const showClose = isMobile || pageId === personalWikiId;
  return (
    <ConversationContext.Provider
      value={{
        ...context,
        openAICoverToShare,
        updateBlocks,
        onSaveArticle,
        renderPageIcon,
        updateContext,
        navigateToChat,
        openUpgrade,
        onSummarize,
        onRenderBlockPreview,
        insertParagraph: !readonly && block?.type === BlockType.PAGE ? insertParagraph : undefined,
      }}
    >
      <NextModalProvider>
        <LiteChatPageWithI18n onClose={showClose ? onClose : undefined} />
      </NextModalProvider>
    </ConversationContext.Provider>
  );
};
